import {
    EVENT_STATUS,
    commentIndicatesCricketMatchResult,
    eventStatusToNumber,
    isMatchFinishedStatus,
} from "../../Trader/Cricket/decimalEventStatus";
import {
    getEventStatusDisplayLabel,
    looksLikeLiveCricketScoreboard,
} from "../../Bullion/lib/bullionPlayedMatchDetails";
import { normalizeMatchStatus } from "../../Bullion/lib/bullionPlayedSlipDisplay";
import { formatCricketSentenceCase } from "../../Games/MatchCards/matchCardCompactUi";
import { SIXES_MARKET_CELL_COUNT, marketCellIndexFromGrid } from "./sixesGridLayout";

export type SixesMatchStatus = "SCHEDULED" | "IN-PLAY" | "BREAK IN PLAY" | "FINISHED";

/** True when Decimal / fixture text indicates a stoppage (rain, drinks, innings break). */
export function sixesStatusTextIndicatesBreakInPlay(raw: unknown): boolean {
    const s = String(raw ?? "").trim();
    if (!s) return false;
    return normalizeMatchStatus(s) === "BREAK IN PLAY";
}

/**
 * Human-readable break label for the tab row — preserves rain / delay detail when present.
 * @param {unknown[]} sources — scorecard event_status, live header status, fixture status, etc.
 */
export function formatSixesBreakInPlayDisplayLabel(...sources: unknown[]): string {
    for (const raw of sources) {
        const s = String(raw ?? "").trim();
        if (!s || !sixesStatusTextIndicatesBreakInPlay(s)) continue;
        const withoutParen = s.replace(/\s*\([^)]*\)\s*/g, " ").replace(/\s+/g, " ").trim();
        if (/break in play/i.test(withoutParen)) {
            const parts = withoutParen.split(/\s*-\s*/);
            const tail = parts.slice(1).join(" - ").trim();
            if (tail) return `MATCH BREAK - ${tail.toUpperCase()}`;
            return "MATCH BREAK";
        }
        if (/rain delay|bad weather|weather delay|toss delayed|wet outfield/i.test(s)) {
            return "MATCH BREAK - RAIN DELAY";
        }
        if (/drinks/i.test(s)) return "MATCH BREAK - DRINKS";
        return "MATCH BREAK";
    }
    return "MATCH BREAK";
}

/**
 * Lobby / tab-row label from fixture or live feed text (uppercase, matches existing Sixes badges).
 */
export function formatSixesFixtureStatusDisplayLabel(fixtureStatus: unknown): string | null {
    const s = String(fixtureStatus ?? "").trim();
    if (!s) return null;
    const normalized = normalizeMatchStatus(s);
    if (normalized === "FINISHED") return "FINISHED";
    if (normalized === "IN-PLAY") return "MATCH LIVE";
    if (normalized === "BREAK IN PLAY") return formatSixesBreakInPlayDisplayLabel(s);
    return null;
}

/**
 * Tab-row status text from derived lifecycle + raw feed strings.
 */
export function formatSixesLiveStatusDisplayLabel(params: {
    displayMatchStatus: SixesMatchStatus;
    scheduledStatusLabel?: string;
    scorecardData?: unknown;
    liveStatus?: string;
    fixtureStatusFallback?: string;
    gamePhase?: string;
}): string {
    const {
        displayMatchStatus,
        scheduledStatusLabel = "SCHEDULED",
        scorecardData,
        liveStatus = "",
        fixtureStatusFallback = "",
        gamePhase = "",
    } = params;
    if (displayMatchStatus === "FINISHED") return "FINISHED";
    if (displayMatchStatus === "IN-PLAY") return "MATCH LIVE";
    if (displayMatchStatus === "BREAK IN PLAY") {
        const scorecardEv = (scorecardData as { event_status?: unknown } | null | undefined)?.event_status;
        return formatSixesBreakInPlayDisplayLabel(scorecardEv, liveStatus, fixtureStatusFallback, gamePhase);
    }
    return scheduledStatusLabel;
}

function deriveBreakInPlayFromFeedTexts(...sources: unknown[]): boolean {
    return sources.some((raw) => sixesStatusTextIndicatesBreakInPlay(raw));
}

export type SixesMatchFinishedOutcome = {
    /** Modal headline — e.g. Match Finished, No Result, Match Abandoned */
    title: string;
    /** Optional detail line shown under the headline */
    message: string;
};

function isRrrChaseComment(comment: string): boolean {
    return / require \d+ runs from \d+ ball/i.test(comment);
}

function statusTextIndicatesAbandoned(text: string): boolean {
    const u = text.toUpperCase();
    return u.includes("MATCH ABANDONED") || (/\babandon/.test(u) && !u.includes("NOT ABANDONED"));
}

function statusTextIndicatesNoResult(text: string): boolean {
    return text.toUpperCase().includes("NO RESULT");
}

function statusTextIndicatesRainOrWeather(text: string): boolean {
    const u = text.toUpperCase();
    return (
        u.includes("RAIN DELAY") ||
        u.includes("RAIN") ||
        u.includes("BAD WEATHER") ||
        u.includes("TOSS DELAYED") ||
        u.includes("WET OUTFIELD") ||
        u.includes("BREAK IN PLAY") ||
        u.includes("PLAY SUSPENDED") ||
        u.includes("CURTAILED")
    );
}

/**
 * Headline + detail for the match-finished modal — abandoned, no-result, rain, or normal result line.
 */
export function deriveSixesMatchFinishedOutcome(params: {
    matchComment?: string;
    scorecardData?: unknown;
    liveStatus?: string;
    fixtureStatusFallback?: string;
    gamePhase?: string;
    /** Tab-row label already derived from feed (e.g. BREAK IN PLAY - RAIN DELAY). */
    tabRowStatusLabel?: string;
    /** Rain/DLS format correction applied to the card. */
    formatCorrection?: { cells?: unknown[]; formatOvers?: number } | null;
}): SixesMatchFinishedOutcome {
    const comment = String(params.matchComment ?? "").trim();
    const scorecardEv = String(
        (params.scorecardData as { event_status?: unknown } | null | undefined)?.event_status ?? "",
    ).trim();
    const liveStatus = String(params.liveStatus ?? "").trim();
    const fixtureStatus = String(params.fixtureStatusFallback ?? "").trim();
    const gamePhase = String(params.gamePhase ?? "").trim();
    const tabRow = String(params.tabRowStatusLabel ?? "").trim();
    const feedTexts = [comment, scorecardEv, liveStatus, fixtureStatus, gamePhase, tabRow].filter(
        Boolean,
    );

    if (comment && commentIndicatesCricketMatchResult(comment) && !isRrrChaseComment(comment)) {
        return {
            title: "Match Finished",
            message: formatCricketSentenceCase(comment),
        };
    }

    for (const text of feedTexts) {
        if (statusTextIndicatesNoResult(text)) {
            return {
                title: "No Result",
                message:
                    formatCricketSentenceCase(text) || "This match ended with no result.",
            };
        }
    }

    for (const text of feedTexts) {
        if (statusTextIndicatesAbandoned(text)) {
            return {
                title: "Match Abandoned",
                message: formatCricketSentenceCase(text) || "This match was abandoned.",
            };
        }
    }

    for (const text of feedTexts) {
        if (statusTextIndicatesRainOrWeather(text)) {
            const hasWinningComment =
                comment &&
                commentIndicatesCricketMatchResult(comment) &&
                !isRrrChaseComment(comment);
            return {
                title: hasWinningComment ? "Match Finished" : "No Result",
                message: formatCricketSentenceCase(text),
            };
        }
    }

    if (comment && !isRrrChaseComment(comment)) {
        const u = comment.toUpperCase();
        if (
            u.includes("RAIN") ||
            u.includes("ABANDON") ||
            u.includes("NO RESULT") ||
            u.includes("SUSPEND") ||
            u.includes("CURTAILED")
        ) {
            const title = u.includes("NO RESULT")
                ? "No Result"
                : u.includes("ABANDON")
                  ? "Match Abandoned"
                  : "Match Finished";
            return { title, message: formatCricketSentenceCase(comment) };
        }
    }

    if (scorecardEv && /match complete|event closed/i.test(scorecardEv)) {
        return { title: "Match Finished", message: "" };
    }

    const formatCells = Array.isArray(params.formatCorrection?.cells)
        ? params.formatCorrection!.cells!.length
        : 0;
    if (formatCells > 0) {
        const overs = params.formatCorrection?.formatOvers;
        const oversNote =
            overs != null && Number.isFinite(Number(overs))
                ? ` Reduced to ${Math.round(Number(overs))} overs per side.`
                : "";
        return {
            title: "No Result",
            message: `Rain affected this match.${oversNote} Voided cheers show as VOID on your card.`,
        };
    }

    if (tabRow && statusTextIndicatesRainOrWeather(tabRow)) {
        return {
            title: "No Result",
            message: formatSixesBreakInPlayDisplayLabel(tabRow),
        };
    }

    return { title: "Match Finished", message: "" };
}

/** Per-cell settlement display state (shared by the demo simulation and real settlement). */
export type SixesCellOutcome = "pending" | "lost" | "void" | "win-green" | "win-pink";

/**
 * Derive the match lifecycle status from the live cricket feed — same precedence as
 * `BullionPlayedGameView`, trimmed to the signals available to the Sixes card view.
 */
export function deriveSixesMatchStatus(params: {
    eventStatus: unknown;
    liveStatus: string;
    gamePhase: string;
    matchComment: string;
    scorecardData: unknown;
    scorePrimaryA: string;
    scorePrimaryB: string;
    hasLiveBallFeed: boolean;
    scheduledStartTs: number | null;
    nowTs: number;
    /** DB / card / fixture truth — overrides stale live feed that still says IN-PLAY. */
    forceFinished?: boolean;
    fixtureStatusFallback?: string;
}): SixesMatchStatus {
    const {
        eventStatus,
        liveStatus,
        gamePhase,
        matchComment,
        scorecardData,
        scorePrimaryA,
        scorePrimaryB,
        hasLiveBallFeed,
        scheduledStartTs,
        nowTs,
        forceFinished = false,
        fixtureStatusFallback = "",
    } = params;

    if (forceFinished) return "FINISHED";

    const fixtureStatus = String(fixtureStatusFallback || liveStatus || "").trim();
    const uFixture = fixtureStatus.toUpperCase();
    if (
        uFixture.includes("MATCH COMPLETE") ||
        uFixture.includes("EVENT CLOSED") ||
        uFixture.includes("MATCH ABANDONED") ||
        (uFixture.includes("FINISHED") && !uFixture.includes("NOT FINISHED"))
    ) {
        return "FINISHED";
    }

    const scorecardEv = (scorecardData as { event_status?: unknown } | null | undefined)?.event_status;
    if (
        isMatchFinishedStatus(eventStatus) ||
        isMatchFinishedStatus(scorecardEv) ||
        commentIndicatesCricketMatchResult(matchComment)
    ) {
        return "FINISHED";
    }

    const feedStatus = String(liveStatus ?? "").trim();
    if (feedStatus !== "" && isMatchFinishedStatus(liveStatus)) return "FINISHED";
    const uLive = feedStatus.toUpperCase();
    if (
        uLive.includes("MATCH COMPLETE") ||
        uLive.includes("EVENT CLOSED") ||
        uLive.includes("MATCH ABANDONED")
    ) {
        return "FINISHED";
    }
    const gp = String(gamePhase || "").trim();
    if (/\bevent\s*closed\b/i.test(gp) || /\bmatch\s*complete\b/i.test(gp)) return "FINISHED";

    const scorecardEvStr =
        (scorecardData as { event_status?: unknown } | null | undefined)?.event_status != null
            ? String(
                  (scorecardData as { event_status?: unknown }).event_status,
              ).trim()
            : "";

    // Prefer scorecard / fixture break signals before generic in-play heuristics (stale header may lag).
    if (
        deriveBreakInPlayFromFeedTexts(scorecardEvStr, fixtureStatus, feedStatus, gp)
    ) {
        return "BREAK IN PLAY";
    }

    const hasConcreteLiveSignals =
        looksLikeLiveCricketScoreboard(scorePrimaryA, scorePrimaryB) || hasLiveBallFeed;

    // Prefer fixture kickoff: if kickoff is still in the future, force pre-match.
    if (scheduledStartTs != null && scheduledStartTs > nowTs) return "SCHEDULED";

    const fromEvent = getEventStatusDisplayLabel(eventStatus);
    if (fromEvent === "FINISHED") return "FINISHED";
    if (fromEvent === "BREAK IN PLAY") return "BREAK IN PLAY";
    if (fromEvent === "IN-PLAY") return "IN-PLAY";

    const fromScorecardEvent = getEventStatusDisplayLabel(scorecardEvStr);
    if (fromScorecardEvent === "BREAK IN PLAY") return "BREAK IN PLAY";
    if (fromScorecardEvent === "IN-PLAY") return "IN-PLAY";

    if (feedStatus.includes("Match in Progress")) return "IN-PLAY";
    if (gp === "In Play" || gp.toLowerCase().includes("in play")) return "IN-PLAY";

    const esNum = eventStatusToNumber(eventStatus);
    if (esNum != null && esNum !== EVENT_STATUS.MATCH_SCHEDULED) {
        if (esNum === EVENT_STATUS.BREAK_IN_PLAY) return "BREAK IN PLAY";
        if (
            esNum === EVENT_STATUS.MATCH_COMPLETE ||
            esNum === EVENT_STATUS.EVENT_CLOSED ||
            esNum === EVENT_STATUS.EJECTED
        ) {
            return "FINISHED";
        }
        return "IN-PLAY";
    }

    if (hasConcreteLiveSignals) return "IN-PLAY";

    const fromText = normalizeMatchStatus(liveStatus);
    if (fromText === "FINISHED") return "FINISHED";
    const kickoffPast = scheduledStartTs == null || scheduledStartTs <= nowTs;
    if (fromText === "SCHEDULED" && kickoffPast) {
        // Without live signals, do not assume IN-PLAY after kickoff (finished feeds often stay "scheduled").
        return hasConcreteLiveSignals ? "IN-PLAY" : "SCHEDULED";
    }
    return fromText;
}

export function sixesMatchStatusIsLive(status: SixesMatchStatus): boolean {
    return status === "IN-PLAY" || status === "BREAK IN PLAY";
}

/**
 * Pull a toss-result line (e.g. "India won the toss and elected to bat") out of the feed's
 * `match_comment` so it can be shown before play begins. Returns "" when the comment isn't a toss line
 * (avoids surfacing chase/RRR/result comments pre-match).
 */
export function extractSixesTossComment(matchComment: string | null | undefined): string {
    const s = String(matchComment ?? "").trim();
    if (!s) return "";
    // Pre-toss scorecards use "0 won the toss…" as a placeholder.
    if (/^\d+\s+won the toss\b/i.test(s)) return "";
    const isToss =
        /\bwon\s+the\s+toss\b/i.test(s) ||
        /\b(elected|chose|opt(?:ed)?|decided)\s+to\s+(bat|bowl|field)\b/i.test(s);
    return isToss ? s : "";
}

export function isSixesCellWon(outcome: SixesCellOutcome): boolean {
    return outcome === "win-pink" || outcome === "win-green";
}

export function isSixesCellVoid(outcome: SixesCellOutcome): boolean {
    return outcome === "void";
}

/**
 * Card lines (rows, columns, both diagonals) as arrays of market-cell indices, excluding the
 * centre 2×2 multiplier block — mirrors backend `sixesCardLineDefinitions` for set detection.
 */
export function sixesCardLineIndices(): number[][] {
    const lines: number[][] = [];
    for (let row = 0; row < 6; row += 1) {
        const cells: number[] = [];
        for (let col = 0; col < 6; col += 1) {
            const idx = marketCellIndexFromGrid(row, col);
            if (idx >= 0) cells.push(idx);
        }
        if (cells.length >= 2) lines.push(cells);
    }
    for (let col = 0; col < 6; col += 1) {
        const cells: number[] = [];
        for (let row = 0; row < 6; row += 1) {
            const idx = marketCellIndexFromGrid(row, col);
            if (idx >= 0) cells.push(idx);
        }
        if (cells.length >= 2) lines.push(cells);
    }
    const diag1: number[] = [];
    const diag2: number[] = [];
    for (let i = 0; i < 6; i += 1) {
        const a = marketCellIndexFromGrid(i, i);
        if (a >= 0) diag1.push(a);
        const b = marketCellIndexFromGrid(i, 5 - i);
        if (b >= 0) diag2.push(b);
    }
    if (diag1.length >= 2) lines.push(diag1);
    if (diag2.length >= 2) lines.push(diag2);
    return lines;
}

export function isCardLineFullyWon(
    line: readonly number[],
    outcomes: readonly SixesCellOutcome[],
): boolean {
    return line.length >= 2 && line.every((idx) => isSixesCellWon(outcomes[idx] ?? "pending"));
}

export function cardLineCelebrationKey(line: readonly number[]): string {
    return line.join(",");
}

/** Lines that just became a full win (backend "set") since the previous outcome snapshot. */
export function findNewlyCompletedCardLines(
    prev: readonly SixesCellOutcome[],
    next: readonly SixesCellOutcome[],
): number[][] {
    const newly: number[][] = [];
    for (const line of sixesCardLineIndices()) {
        if (!isCardLineFullyWon(line, prev) && isCardLineFullyWon(line, next)) {
            newly.push([...line]);
        }
    }
    return newly;
}

export type SixesApiSettlementCell = {
    row: number;
    col: number;
    settled?: boolean;
    won?: boolean;
    settlementOutcome?: string | null;
    settlementResult?: string | null;
};

/**
 * Map each cell's settlement result onto its grid index (for the cell detail modal stat box).
 * Uses any captured result from the server, including in-progress values when present.
 */
export function buildSixesResultsFromApiCells(
    cells: ReadonlyArray<SixesApiSettlementCell>,
): (string | null)[] {
    const results: (string | null)[] = Array.from(
        { length: SIXES_MARKET_CELL_COUNT },
        () => null,
    );
    for (const cell of cells) {
        const idx = marketCellIndexFromGrid(Number(cell.row), Number(cell.col));
        if (idx < 0) continue;
        const value =
            cell.settlementResult != null && String(cell.settlementResult).trim() !== ""
                ? String(cell.settlementResult).trim()
                : null;
        if (value) results[idx] = value;
    }
    return results;
}

/** Map one API cell to a grid outcome (void vs lost vs win). */
export function resolveSixesCellOutcomeFromApiCell(cell: SixesApiSettlementCell): SixesCellOutcome {
    if (!cell.settled) return "pending";
    if (cell.won) return "win-pink";
    const outcome = String(cell.settlementOutcome ?? "").trim().toUpperCase();
    if (outcome === "VOID") return "void";
    const result = String(cell.settlementResult ?? "").trim().toLowerCase();
    if (result === "void" || result === "reduced overs") return "void";
    return "lost";
}

/** Map server settlement to grid outcomes — wins are pink only (green is applied in the UI). */
export function buildSixesPinkOutcomesFromApiCells(
    cells: ReadonlyArray<SixesApiSettlementCell>,
): SixesCellOutcome[] {
    const outcomes: SixesCellOutcome[] = Array.from(
        { length: SIXES_MARKET_CELL_COUNT },
        () => "pending",
    );
    for (const cell of cells) {
        const idx = marketCellIndexFromGrid(Number(cell.row), Number(cell.col));
        if (idx < 0) continue;
        outcomes[idx] = resolveSixesCellOutcomeFromApiCell(cell);
    }
    return outcomes;
}

/** Promote every cell in a fully-won card line to green (backend "set"). */
export function promoteCardLineWinsToGreen(outcomes: readonly SixesCellOutcome[]): SixesCellOutcome[] {
    const next = [...outcomes];
    for (const line of sixesCardLineIndices()) {
        if (!isCardLineFullyWon(line, next)) continue;
        for (const idx of line) {
            if (next[idx] === "win-pink") next[idx] = "win-green";
        }
    }
    return next;
}

/** Pink wins plus green on completed card lines (static / skip-animation entry). */
export function buildSixesOutcomesFromApiCells(
    cells: ReadonlyArray<SixesApiSettlementCell>,
): SixesCellOutcome[] {
    return promoteCardLineWinsToGreen(buildSixesPinkOutcomesFromApiCells(cells));
}

/** True when the server already settled at least one market cell and the card has scored coins (e.g. toss). */
export function sixesCardHasPrematchSettlementWithPoints(data: {
    card: { totalCoins?: number };
    cells: ReadonlyArray<{ isMultiplier?: boolean; settled?: boolean }>;
}): boolean {
    const coins = Math.round(Number(data.card.totalCoins) || 0);
    if (coins <= 0) return false;
    return data.cells.some((cell) => !cell.isMultiplier && Boolean(cell.settled));
}

/** True when the live board already reflects a prematch settlement that scored coins. */
export function sixesBoardHasPrematchSettlementWithPoints(
    cellOutcomes: readonly SixesCellOutcome[],
    coinsTotal: number,
): boolean {
    if (coinsTotal <= 0) return false;
    return cellOutcomes.some((outcome) => outcome !== "pending");
}
